/** @jsxImportSource @emotion/react */
import { FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react';
import { Global, ThemeProvider } from '@zip/business-components';
import {
  AnalyticsProvider,
  Auth0Provider,
  CookiesProvider,
  FeatureFlagsProvider,
  MerchantDataProvider,
  NavigationProvider,
  PageTracking,
  SnackbarProvider,
} from 'contexts';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { theme } from 'global';
import Layout from 'global/layout';
import { FC } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {
  createTheme,
  ThemeProvider as MuiBreakpointsThemeProvider,
  Theme,
} from '@mui/material/styles';

/**
 * Note: This is specifically for overriding MUI's default breakpoints with our own definitions
 * This mainly affects MuiGrid sizing and our Hidden component for responsive design
 *  - Business Components' ThemeProvider provides style, colours, theming
 *  - MuiBreakpointsThemeProvider is only to align Breakpoints definition, inheriting Business Component's themes
 * More about nesting ThemeProviders here: https://mui.com/material-ui/customization/theming/#nesting-the-theme
 */
export const breakpointsOverride = (defaultTheme: Theme) => {
  return createTheme({
    ...defaultTheme,
    breakpoints: {
      values: {
        xs: theme.breaks.xs,
        sm: theme.breaks.sm,
        md: theme.breaks.md,
        lg: theme.breaks.lg,
        xl: theme.breaks.xl,
      },
    },
  });
};

const App: FC = () => (
  <BrowserRouter>
    <AnalyticsProvider>
      <PageTracking>
        <FpjsProvider
          loadOptions={{
            apiKey: process.env.REACT_APP_FPJS_PUBLIC_KEY,
            endpoint: process.env.REACT_APP_FPJS_ENDPOINT,
          }}
        >
          <Auth0Provider
            domain={process.env.REACT_APP_AUTH0_DOMAIN}
            clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
            redirectUri={`${window.location.origin}/callback`}
          >
            <CookiesProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <ThemeProvider theme={theme}>
                  <MuiBreakpointsThemeProvider
                    theme={(defaultTheme: Theme) =>
                      breakpointsOverride(defaultTheme)
                    }
                  >
                    <Global />
                    <SnackbarProvider>
                      <MerchantDataProvider>
                        <FeatureFlagsProvider>
                          <NavigationProvider>
                            <Layout />
                          </NavigationProvider>
                        </FeatureFlagsProvider>
                      </MerchantDataProvider>
                    </SnackbarProvider>
                  </MuiBreakpointsThemeProvider>
                </ThemeProvider>
              </LocalizationProvider>
            </CookiesProvider>
          </Auth0Provider>
        </FpjsProvider>
      </PageTracking>
    </AnalyticsProvider>
  </BrowserRouter>
);

export default App;
